import React, { memo, useCallback, useMemo } from 'react';

import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import PropTypes from 'prop-types';

import { getLabelColors } from 'constants/colors';

import {
  ENTITY_REFERENCE_COLOR_NAME_BY_TYPE,
  ENTITY_REFERENCE_ICON_BY_TYPE,
  ENTITY_REFERENCE_TYPE_ORDER,
  getEntityReferenceKey,
} from '../TeamChat.entityReferences.utils';

import {
  ENTITY_SUGGESTIONS_EMPTY_LABEL,
  ENTITY_SUGGESTIONS_ICON_SIZE,
  ENTITY_SUGGESTIONS_KEYBOARD_HINTS,
  ENTITY_SUGGESTIONS_LOADING_LABEL,
  ENTITY_SUGGESTIONS_LOADING_SPINNER_SIZE,
  ENTITY_SUGGESTIONS_TITLE,
} from './TeamChatEntitySuggestions.constants';
import {
  emptyStateStyles,
  focusedItemStyles,
  footerStyles,
  groupHeaderStyles,
  hintKeyStyles,
  hintStyles,
  itemStyles,
  listStyles,
  loadingRowStyles,
  primaryTextStyles,
  rootStyles,
  secondaryTextStyles,
  textColumnStyles,
  titleStyles,
} from './TeamChatEntitySuggestions.styles';
import { TeamChatEntitySuggestionsTestIds } from './TeamChatEntitySuggestions.testIds';

const TeamChatEntitySuggestions = memo(
  ({
    groups,
    focusedEntityKey = null,
    query = '',
    onSelectEntity,
    isHidden = false,
    isLoading = false,
  }) => {
    const { palette } = useTheme();

    // Precomputed once per theme mode (not per row) so each icon gets a stable style object.
    const iconStyleByType = useMemo(
      () =>
        Object.fromEntries(
          ENTITY_REFERENCE_TYPE_ORDER.map(type => [
            type,
            {
              color: getLabelColors(
                ENTITY_REFERENCE_COLOR_NAME_BY_TYPE[type],
                palette.mode === 'dark',
              )?.color,
            },
          ]),
        ),
      [palette.mode],
    );

    const onItemMouseDown = useCallback(
      item => e => {
        e.preventDefault();
        onSelectEntity(item);
      },
      [onSelectEntity],
    );

    if (isHidden) {
      return null;
    }

    const hasGroups = !isLoading && !!groups.length;
    const trimmedQuery = query.trim();
    const emptyLabel = trimmedQuery
      ? `${ENTITY_SUGGESTIONS_EMPTY_LABEL} for "${trimmedQuery}"`
      : `${ENTITY_SUGGESTIONS_EMPTY_LABEL}.`;
    const visibleHints = hasGroups
      ? ENTITY_SUGGESTIONS_KEYBOARD_HINTS
      : ENTITY_SUGGESTIONS_KEYBOARD_HINTS.filter(hint => hint.id === 'dismiss');

    return (
      <Box data-testid={TeamChatEntitySuggestionsTestIds.ROOT} sx={rootStyles}>
        <Typography
          color="text.secondary"
          data-testid={TeamChatEntitySuggestionsTestIds.TITLE}
          sx={titleStyles}
          variant="medium12"
        >
          {ENTITY_SUGGESTIONS_TITLE}
        </Typography>
        {isLoading && (
          <Box data-testid={TeamChatEntitySuggestionsTestIds.LOADING} sx={loadingRowStyles}>
            <CircularProgress size={ENTITY_SUGGESTIONS_LOADING_SPINNER_SIZE} />
            <Typography color="text.secondary" variant="regular14">
              {ENTITY_SUGGESTIONS_LOADING_LABEL}
            </Typography>
          </Box>
        )}
        {hasGroups && (
          <Box role="listbox" sx={listStyles}>
            {groups.map(group => {
              const Icon = ENTITY_REFERENCE_ICON_BY_TYPE[group.type];
              const iconStyle = iconStyleByType[group.type];

              return (
                <Box key={group.type}>
                  <Typography sx={groupHeaderStyles} variant="medium12">
                    {group.label}
                  </Typography>
                  {group.items.map(item => {
                    const key = getEntityReferenceKey(item);
                    const isFocused = key === focusedEntityKey;

                    return (
                      <Box
                        aria-selected={isFocused}
                        data-testid={TeamChatEntitySuggestionsTestIds.ITEM}
                        key={key}
                        role="option"
                        sx={isFocused ? focusedItemStyles : itemStyles}
                        onMouseDown={onItemMouseDown(item)}
                      >
                        <Icon size={ENTITY_SUGGESTIONS_ICON_SIZE} style={iconStyle} />
                        <Box sx={textColumnStyles}>
                          <Typography sx={primaryTextStyles} variant="regular14">
                            {item.label}
                          </Typography>
                          {!!item.secondary && (
                            <Typography
                              color="text.secondary"
                              sx={secondaryTextStyles}
                              variant="regular12"
                            >
                              {item.secondary}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        )}
        {!isLoading && !hasGroups && (
          <Typography
            data-testid={TeamChatEntitySuggestionsTestIds.EMPTY}
            sx={emptyStateStyles}
            variant="regular14"
          >
            {emptyLabel}
          </Typography>
        )}
        <Box sx={footerStyles}>
          {visibleHints.map(hint => (
            <Box key={hint.id} sx={hintStyles}>
              <Box component="span" sx={hintKeyStyles}>
                {hint.key}
              </Box>
              <Typography variant="regular12">{hint.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  },
);
TeamChatEntitySuggestions.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(PropTypes.object).isRequired,
    }),
  ).isRequired,
  onSelectEntity: PropTypes.func.isRequired,
  focusedEntityKey: PropTypes.string,
  query: PropTypes.string,
  isHidden: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default TeamChatEntitySuggestions;
